import { useState, useEffect } from 'react';
import { useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import {
  Clock,
  ChevronRight,
  CheckCircle2,
  Check,
  ArrowLeft,
  Home,
} from 'lucide-react';
import { getArticleContent as loadArticleContent, type Article, type AreaKey as LoaderAreaKey } from '@/lib/knowledge-loader';
import { areaConfig } from '@/config/area-config';
import OfficialSourcesNotice from '@/components/OfficialSourcesNotice';
import { getNativePlatform } from '@/utils/native-platform';

const levelLabels: Record<string, string> = {
  '7d': '7 dagar',
  '30d': '30 dagar',
  '90d': '90 dagar+',
};

const levelColors: Record<string, string> = {
  '7d': 'bg-green-500/20 text-green-400 border-green-500/30',
  '30d': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  '90d': 'bg-red-500/20 text-red-400 border-red-500/30',
};

interface ArticleModalProps {
  article: Article;
  areaKey: LoaderAreaKey;
  isRead: boolean;
  onClose: () => void;
  onMarkAsRead: () => void;
  onNextArticle?: () => void;
  onHome?: () => void;
}

export default function ArticleModal({
  article,
  areaKey,
  isRead,
  onClose,
  onMarkAsRead,
  onNextArticle,
  onHome,
}: ArticleModalProps) {
  const nativePlatform = getNativePlatform();
  const isNativeIOS = nativePlatform === 'ios';

  const config = areaConfig[areaKey as keyof typeof areaConfig] || {
    icon: () => null, 
    color: '#6b6b6b' 
  };
  const Icon = config.icon;

  // Lås body scroll när modalen är öppen
  useLayoutEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousDocOverflow = document.documentElement.style.overflow;

    if (isNativeIOS) {
      try {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      } catch (_e) {
        void _e;
      }
    } else {
      const y = window.scrollY || window.pageYOffset || 0;
      try { document.body.dataset.prevScrollY = String(y); } catch (_e) { void _e; }
      try { lockScroll(); } catch (_e) { try { document.body.style.overflow = 'hidden'; } catch { /* noop */ } }
      try { requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch { /* noop */ } }); } catch { /* noop */ }
    }
    
    // Prevent touch scrolling on background (especially for mobile)
    const preventTouch = (e: TouchEvent) => {
      // Allow scrolling inside the modal, but prevent it on the background
      const target = e.target as HTMLElement;
      if (!target.closest('.article-modal-content')) {
        e.preventDefault();
      }
    };
    
    if (!isNativeIOS) {
      document.addEventListener('touchmove', preventTouch, { passive: false });
    }
    
    return () => {
      if (isNativeIOS) {
        try {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousDocOverflow;
        } catch (_e) {
          void _e;
        }
      } else {
        try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
        document.removeEventListener('touchmove', preventTouch);
      }
    };
  }, [isNativeIOS]);

  // Scrolla till toppen när artikeln ändras
  useEffect(() => {
    const modalContent = document.querySelector('.article-modal-content');
    if (modalContent) {
      modalContent.scrollTop = 0;
    }
  }, [article.id]);

  // Ladda artikelinnehåll asynkront
  const [articleContent, setArticleContent] = useState<string>('Laddar...');
  
  useEffect(() => {
    const loadContent = async () => {
      try {
        const content = await loadArticleContent(article.id);
        setArticleContent(content);
      } catch (error) {
        console.error('Failed to load article:', error);
        setArticleContent(`
${article.summary}

## Innehåll kunde inte laddas

Det uppstod ett fel när artikelinnehållet skulle laddas. Försök igen senare.

## Grundläggande information

${article.summary}
        `.trim());
      }
    };
    
    loadContent();
  }, [article.id, article.summary]);

  return createPortal(
    <div className="fixed z-50 bg-background overflow-y-auto article-modal-content" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
      {/* Header - sticky */}
      <div className="sticky top-0 z-10 flex items-center gap-4 border-b border-border bg-card p-4">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="hidden sm:inline">Tillbaka</span>
        </button>
        {onHome && (
          <button
            onClick={onHome}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            title="Hem till kunskap"
          >
            <Home className="h-5 w-5" />
            <span className="hidden sm:inline">Hem</span>
          </button>
        )}
      </div>

      {/* Artikel innehåll */}
      <div className="max-w-4xl mx-auto">
        {/* Artikel header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="rounded-lg p-2"
              style={{ backgroundColor: `${config.color}20` }}
            >
              <Icon className="h-5 w-5" style={{ color: config.color }} />
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs border ${levelColors[article.level]}`}>
              {levelLabels[article.level]}
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              {article.readTime} min läsning
            </span>
          </div>
          <h1 className="text-3xl font-bold">{article.title}</h1>
        </div>

        {/* Artikelinnehåll */}
        <div className="p-6 prose prose-invert max-w-none">
          {articleContent.split('\n\n').map((paragraph, i) => {
            const trimmedParagraph = paragraph.trim();
            
            // Horizontal rule (---, ___, eller ***)
            if (trimmedParagraph === '---' || trimmedParagraph === '___' || trimmedParagraph === '***') {
              return <hr key={i} className="my-6 border-t border-border" />;
            }
            
            // H1 rubriker (#)
            if (trimmedParagraph.startsWith('# ') && !trimmedParagraph.startsWith('## ')) {
              return (
                <h1 key={i} className="text-2xl font-bold mt-8 mb-4 text-foreground">
                  {trimmedParagraph.replace('# ', '')}
                </h1>
              );
            }
            
            // H2 rubriker (##)
            if (trimmedParagraph.startsWith('## ')) {
              return (
                <h2 key={i} className="text-xl font-semibold mt-6 mb-3 text-foreground">
                  {trimmedParagraph.replace('## ', '')}
                </h2>
              );
            }
            
            // H3 rubriker (###)
            if (trimmedParagraph.startsWith('### ')) {
              return (
                <h3 key={i} className="text-lg font-semibold mt-4 mb-2 text-foreground">
                  {trimmedParagraph.replace('### ', '')}
                </h3>
              );
            }
            
            // Blockquotes (börjar med >)
            if (trimmedParagraph.startsWith('>')) {
              const lines = trimmedParagraph.split('\n').map(line => line.replace(/^>\s?/, '').trim()).filter(line => line);
              return (
                <blockquote key={i} className="border-l-4 border-primary/50 pl-4 py-3 my-3 bg-muted/30 rounded-r">
                  {lines.map((line, j) => {
                    // Hantera fetstil i blockquotes
                    const parts = line.split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <p key={j} className="text-muted-foreground text-base leading-normal my-0">
                        {parts.map((part, k) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={k} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
                          }
                          return <span key={k}>{part}</span>;
                        })}
                      </p>
                    );
                  })}
                </blockquote>
              );
            }
            
            // Paragrafer som börjar med fetstil (t.ex. "**Steg 1:**")
            if (trimmedParagraph.startsWith('**')) {
              const parts = trimmedParagraph.split(/(\*\*[^*]+\*\*)/g);
              return (
                <p key={i} className="text-muted-foreground text-base leading-relaxed mb-3 font-medium">
                  {parts.map((part, j) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={j} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
                    }
                    return <span key={j}>{part}</span>;
                  })}
                </p>
              );
            }
            
            // Punktlistor (börjar med -, *, eller •)
            if (trimmedParagraph.match(/^[-•*]\s/m)) {
              const items = trimmedParagraph.split('\n').filter(line => {
                const trimmed = line.trim();
                return trimmed.startsWith('- ') || trimmed.startsWith('• ') || (trimmed.startsWith('* ') && !trimmed.startsWith('** '));
              });
              return (
                <ul key={i} className="list-disc list-inside space-y-1 text-muted-foreground mb-4">
                  {items.map((item, j) => {
                    const text = item.replace(/^[-•*]\s/, '').trim();
                    // Hantera fetstil i listorna
                    const parts = text.split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <li key={j} className="text-base">
                        {parts.map((part, k) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={k} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
                          }
                          return <span key={k}>{part}</span>;
                        })}
                      </li>
                    );
                  })}
                </ul>
              );
            }
            
            // Numrerade listor
            if (trimmedParagraph.match(/^\d\./m)) {
              const items = trimmedParagraph.split('\n').filter(line => line.trim().match(/^\d+\./));
              return (
                <ol key={i} className="list-decimal list-inside space-y-1 text-muted-foreground mb-4">
                  {items.map((item, j) => {
                    // Ta bort siffran och punkten från början
                    const text = item.replace(/^\d+\.\s*/, '').trim();
                    
                    // Hantera fetstil i listorna
                    const parts = text.split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <li key={j} className="text-base">
                        {parts.map((part, k) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={k} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
                          }
                          return <span key={k}>{part}</span>;
                        })}
                      </li>
                    );
                  })}
                </ol>
              );
            }
            
            // Checkboxar (☐)
            if (trimmedParagraph.includes('☐') || trimmedParagraph.includes('☑') || trimmedParagraph.includes('✅') || trimmedParagraph.includes('❌') || trimmedParagraph.includes('⏳')) {
              const items = trimmedParagraph.split('\n').filter(line => line.trim().match(/^[☐☑✅❌⏳]/));
              return (
                <ul key={i} className="space-y-1 text-muted-foreground mb-4">
                  {items.map((item, j) => (
                    <li key={j} className="text-base flex items-start gap-2">
                      <span className="shrink-0">{item.trim()[0]}</span>
                      <span>{item.trim().slice(1).trim()}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            
            // Vanliga paragrafer med fetstil
            const parts = trimmedParagraph.split(/(\*\*[^*]+\*\*)/g);
            return (
              <p key={i} className="text-muted-foreground text-base leading-relaxed mb-3">
                {parts.map((part, j) => {
                  if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={j} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
                  }
                  return <span key={j}>{part}</span>;
                })}
              </p>
            );
          })}

          <div className="not-prose mt-8 space-y-3 border-t border-border pt-6">
            {isRead ? (
              <button
                onClick={onMarkAsRead}
                className="w-full flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30 hover:bg-green-500/5 transition-colors group"
              >
                <CheckCircle2 className="h-6 w-6 text-green-500 shrink-0" />
                <div className="text-left flex-1">
                  <p className="font-medium text-green-400">Du har läst denna artikel</p>
                  <p className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                    Klicka för att avmarkera
                  </p>
                </div>
              </button>
            ) : (
              <button
                onClick={onMarkAsRead}
                className="w-full flex items-center justify-center gap-3 p-4 rounded-lg bg-primary/10 border border-primary/30 hover:bg-primary/20 transition-colors group"
              >
                <div className="w-6 h-6 rounded border-2 border-primary flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Check className="h-4 w-4 text-primary opacity-0 group-hover:opacity-50 transition-opacity" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Jag har läst och förstått innehållet</p>
                </div>
              </button>
            )}

            {onNextArticle && (
              <button
                onClick={onNextArticle}
                className="w-full flex items-center justify-between gap-3 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-card transition-colors group"
              >
                <span className="font-medium">Nästa artikel</span>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </button>
            )}

            <div className="pt-8 sm:pt-10">
              <div className="mb-6 border-t border-border" />
              <OfficialSourcesNotice
                compact
                title="Kontrollera alltid officiella källor"
                description="Kunskapsbanken är vägledande. För aktuella råd, instruktioner och lägesbilder ska du alltid kontrollera officiella källor."
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
